import React, { useState } from 'react';
import { Star, ExternalLink, Quote, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { testimonials } from '../mock';
import ApplicationForm from './ApplicationForm';
import { Dialog, DialogContent } from './ui/dialog';
import Reveal from './shared/Reveal';
import SectionHeader from './shared/SectionHeader';

const REVIEWS_URL =
  'https://www.google.com/search?q=Xpert+Advisor+%E2%80%93+Study+Abroad+%7C+Head+Office+Hyderabad+Reviews';

const Testimonials = () => {
  const [applyFormOpen, setApplyFormOpen] = useState(false);
  const [qrCodeOpen, setQrCodeOpen] = useState(false);

  return (
    <>
      <section className="py-20 lg:py-28 bg-white">
        <div className="container-x">
          <SectionHeader
            eyebrow="Student stories"
            title={<>Trusted by students, <span className="text-brand-500">rated 5 stars</span></>}
            subtitle="Real reviews from our Google Business Profile — unedited, from students we've guided."
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6 items-stretch">
            {testimonials.map((testimonial, index) => (
              <Reveal key={testimonial.id} delay={index * 110} className="h-full">
                <figure className="card-premium relative p-7 h-full flex flex-col">
                  <Quote className="w-8 h-8 text-brand-100" aria-hidden="true" />
                  <blockquote className="mt-3 text-navy-800 leading-relaxed flex-1">
                    “{testimonial.testimonial}”
                  </blockquote>
                  <figcaption className="mt-6 pt-5 border-t border-navy-50">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-display font-bold text-navy-900 text-sm">{testimonial.name}</p>
                        <p className="text-xs text-slate-400 mt-0.5">{testimonial.program}</p>
                      </div>
                      <div className="flex gap-0.5">
                        {[...Array(testimonial.rating)].map((_, idx) => (
                          <Star key={idx} className="w-3.5 h-3.5 text-brand-500 fill-brand-500" />
                        ))}
                      </div>
                    </div>
                  </figcaption>
                </figure>
              </Reveal>
            ))}

            {/* Google reviews QR tile */}
            <Reveal delay={330} className="h-full">
              <button
                onClick={() => setQrCodeOpen(true)}
                className="card-premium w-full h-full p-7 flex flex-col items-center justify-center text-center"
              >
                <img
                  src="/Google.png"
                  alt="QR code linking to Google Reviews"
                  className="w-32 h-32 object-contain"
                />
                <p className="mt-4 font-display font-bold text-navy-900 text-sm">Scan for more reviews</p>
                <p className="text-xs text-slate-400 mt-1">Google Business Profile</p>
              </button>
            </Reveal>
          </div>

          <Reveal className="text-center mt-10">
            <a href={REVIEWS_URL} target="_blank" rel="noopener noreferrer" className="btn-outline-navy">
              See All Reviews
              <ExternalLink className="w-4 h-4" />
            </a>
          </Reveal>

          {/* Bottom CTA */}
          <Reveal className="mt-16 lg:mt-20">
            <div className="relative rounded-3xl bg-navy-900 overflow-hidden text-center px-8 py-14 lg:py-16 shadow-lift">
              <div className="absolute -top-24 left-1/4 w-72 h-72 rounded-full bg-brand-500/25 blur-3xl"></div>
              <div className="absolute -bottom-24 right-1/4 w-72 h-72 rounded-full bg-navy-500/40 blur-3xl"></div>
              <div className="relative">
                <h3 className="font-display text-3xl lg:text-4xl font-bold text-white">
                  Ready to write <span className="text-brand-400">your</span> success story?
                </h3>
                <p className="mt-4 text-navy-100/80 max-w-2xl mx-auto leading-relaxed">
                  Join the students who turned their study-abroad dream into a boarding pass with Xpert Advisor.
                </p>
                <div className="mt-9 flex flex-col sm:flex-row gap-4 justify-center">
                  <button onClick={() => setApplyFormOpen(true)} className="btn-primary text-base px-9">
                    Start Your Application
                  </button>
                  <Link to="/universities" className="btn-outline-light text-base px-9">
                    Explore Universities
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <ApplicationForm open={applyFormOpen} onOpenChange={setApplyFormOpen} />

      {/* QR Code Modal */}
      <Dialog open={qrCodeOpen} onOpenChange={setQrCodeOpen}>
        <DialogContent className="sm:max-w-[480px] max-w-[95vw] rounded-2xl">
          <div className="flex flex-col items-center text-center pt-2">
            <h3 className="font-display text-2xl font-bold text-navy-900">Scan for our reviews</h3>
            <p className="text-sm text-slate-500 mt-1.5 mb-6">
              Point your phone camera at the code to read all our Google reviews
            </p>
            <div className="bg-white p-5 rounded-2xl border border-navy-100 shadow-card">
              <img
                src="/Google.png"
                alt="QR code for Google Reviews"
                className="w-60 h-60 object-contain"
              />
            </div>
            <a
              href={REVIEWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary mt-7"
            >
              View on Google
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Testimonials;
